import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router';
import moment from 'moment';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TablePagination,
  Grid,
  TextField,
} from '@mui/material';

import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import { Visibility } from '@material-ui/icons';

import { Tooltip } from '@material-ui/core';
import { useStyles, StyledTableCell } from './styles';

import Loader from '../../../components/Loader';

import { Record } from '../../../models/record';

import { useAxios } from '../../../utils/hooks';
import { getChip } from '../../../utils/getChip';
import Select from '../../../components/Select';
import { SelectProps } from '../../Records/List/dto';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';

function getSufix(label: string) {
  return label !== 'online' ? ' D' : '';
}

const Supervision: React.FC = () => {
  const classes = useStyles();
  const api = useAxios();
  const history = useHistory();
  const [supervisions, setSupervisions] = useState<Record[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);
  const formRef = useRef<FormHandles>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [patients, setPatients] = useState<SelectProps[]>([]);
  const [numbers, setNumbers] = useState<SelectProps[]>([]);
  const [filters, setFilters] = useState('');
  const [filterTypes, setFilterTypes] = useState({
    type: '',
    status: '',
    patient: '',
    number: '',
    date: '',
  });

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getSupervisions() {
      try {
        const supervisionsResponse = await api.current?.get(
          `/records/supervisors?${filters}`,
        );

        setSupervisions(supervisionsResponse?.data);

        setPatients(
          supervisionsResponse?.data.map((record: Record) => {
            return {
              value: record.patient._id,
              label: record.patient.name,
            };
          }),
        );

        setNumbers(
          supervisionsResponse?.data.map((record: Record) => {
            return {
              value: String(record.number),
              label: record.number,
            };
          }),
        );
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao buscar prontuários.',
        });
      } finally {
        setLoading(false);
      }
    }

    getSupervisions();
  }, [api, filters]);

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFilters = useCallback(
    (type: string, data: any) => {
      setLoading(true);

      switch (type) {
        case 'type':
          setFilterTypes({
            ...filterTypes,
            type: data && data.value ? `&type=${data.value}` : '',
          });
          break;
        case 'status':
          setFilterTypes({
            ...filterTypes,
            status: data && data.value ? `&status=${data.value}` : '',
          });
          break;
        case 'patient':
          setFilterTypes({
            ...filterTypes,
            patient: data && data.value ? `&patient=${data.value}` : '',
          });
          break;
        case 'number':
          setFilterTypes({
            ...filterTypes,
            number: data && data.value ? `&number=${data.value}` : '',
          });
          break;
        case 'date':
          setFilterTypes({
            ...filterTypes,
            date: data ? `&date=${moment(data).format('YYYY-MM-DD')}` : '',
          });
          break;
        default:
          break;
      }
    },
    [filterTypes],
  );

  useEffect(() => {
    setFilters(
      filterTypes.type +
        filterTypes.status +
        filterTypes.patient +
        filterTypes.number +
        filterTypes.date,
    );

    setPage(0);
  }, [filterTypes]);

  return (
    <>
      <Loader loading={loading} />

      <Form ref={formRef} initialData={{}} onSubmit={handleFilters}>
        <Paper elevation={0} className={classes.filterCard}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Tipo</span>
              <Select
                name="type"
                isMulti={false}
                placeholder="Tipo"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('type', e)}
                options={[
                  { value: 'online', label: 'Online' },
                  { value: 'scanned', label: 'Digitalizado' },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Status</span>
              <Select
                name="status"
                isMulti={false}
                placeholder="Status"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('status', e)}
                options={[
                  { value: 'active', label: 'Ativo' },
                  { value: 'closed', label: 'Encerrado' },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Paciente</span>
              <Select
                name="patient"
                isMulti={false}
                placeholder="Paciente"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('patient', e)}
                options={patients}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Número</span>
              <Select
                name="number"
                isMulti={false}
                placeholder="Número"
                isClearable
                isSearchable
                className={classes.filterField}
                onChange={(e) => handleFilters('number', e)}
                options={numbers}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Data</span>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(d) => {
                    setDate(d);
                    handleFilters('date', d);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Paper>
      </Form>

      <TableContainer
        component={Paper}
        style={{ display: !loading ? '' : 'none', marginBottom: '2%' }}
      >
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Terapeuta</StyledTableCell>
              <StyledTableCell>Paciente</StyledTableCell>
              <StyledTableCell>Número</StyledTableCell>
              <StyledTableCell>Última Atualização</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Tipo</StyledTableCell>
              <StyledTableCell align="center">Visualizar</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!supervisions.length && (
              <TableRow>
                <TableCell component="th" scope="row" colSpan={7}>
                  <Typography>Nenhum prontuário encontrado.</Typography>
                </TableCell>
              </TableRow>
            )}

            {(rowsPerPage > 0
              ? supervisions.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : supervisions
            ).map((record) => (
              <TableRow key={record._id}>
                <TableCell component="th" scope="row">
                  <Tooltip title={record?.professional.join(', ')}>
                    <span>
                      {`${record?.professional[0] || '--'}${
                        record?.professional.length > 1 ? ', ...' : ''
                      }`}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  {record?.patient?.name || '--'}

                  {record?.patient2 && ` / ${record?.patient2?.name}`}
                </TableCell>

                <TableCell>
                  {`${record.number}${getSufix(record.type)}`}
                </TableCell>
                <TableCell>
                  {moment(record.updatedAt).format('DD/MM/YYYY HH:mm')}
                </TableCell>
                <TableCell>{getChip(record.status)}</TableCell>
                <TableCell>{getChip(record.type)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => {
                      history.push(`/prontuarios/${record._id}`);
                    }}
                    color="primary"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={supervisions.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={<span>Prontuários por página:</span>}
          labelDisplayedRows={({ count, from, to }) => {
            return `${from} - ${to} de ${count}`;
          }}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default Supervision;
